import { useCallback, useEffect, useRef, useState } from "react";
import { EyeIcon, FileTextIcon, Trash2Icon, UploadIcon } from "lucide-react";
import {
    deleteSupportingFile,
    getMySupportingFiles,
    uploadSupportingFile,
} from "@service/cv";
import { SupportingFile } from "@type/cv";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from "@component/sheet";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@component/alert-dialog";
import { PDFViewer } from "@component/pdf-viewer";
import { Button } from "@component/button";
import { parseFormat } from "@util/date";

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const MAX_FILES = 5;

const formatBytes = (bytes?: number) => {
    if (typeof bytes !== "number" || bytes <= 0) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

interface SupportingDocumentsProps {
    isEditing?: boolean;
}

/**
 * Displays the candidate's previously-uploaded supporting documents on
 * the My Resume page. Allows the user to preview each PDF in a right-
 * side drawer, delete an entry, or upload additional files (subject to
 * the same 5MB / 5-file limits enforced during signup).
 *
 * Currently scoped to the demo-practice flow — caller is expected to
 * gate the mount with VITE_MODE_DEMO_PRACTICE_INTERVIEW.
 */
export function SupportingDocuments({ isEditing }: SupportingDocumentsProps) {
    const [files, setFiles] = useState<SupportingFile[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
    const [previewFile, setPreviewFile] = useState<SupportingFile | null>(null);
    // Document currently queued for deletion — opens the confirm
    // dialog. Cleared once the user cancels or after the delete
    // request completes (success or failure).
    const [pendingDelete, setPendingDelete] = useState<SupportingFile | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const fetchFiles = useCallback(async () => {
        setIsLoading(true);
        setLoadError(null);
        const resp = await getMySupportingFiles();
        if (resp.success) {
            setFiles(resp.data || []);
        } else {
            setLoadError(resp.message || "Failed to load supporting documents.");
        }
        setIsLoading(false);
    }, []);

    useEffect(() => {
        fetchFiles();
    }, [fetchFiles]);

    const handleFiles = useCallback(
        async (incoming: FileList | File[]) => {
            const arr = Array.from(incoming);
            const remaining = MAX_FILES - files.length;
            if (remaining <= 0) {
                setUploadError(`You can upload up to ${MAX_FILES} files only.`);
                return;
            }
            const toUpload = arr.slice(0, remaining);
            const errors: string[] = [];
            const valid: File[] = [];
            toUpload.forEach((file) => {
                const lower = file.name.toLowerCase();
                if (!lower.endsWith(".pdf")) {
                    errors.push(`"${file.name}" is not a PDF.`);
                    return;
                }
                if (file.size > MAX_FILE_SIZE) {
                    errors.push(`"${file.name}" exceeds 5 MB.`);
                    return;
                }
                valid.push(file);
            });
            if (arr.length > remaining) {
                errors.push(`Only the first ${remaining} file(s) were accepted (max ${MAX_FILES}).`);
            }

            setUploadError(errors.length > 0 ? errors.join(" ") : null);

            if (valid.length === 0) return;

            setIsUploading(true);
            const results = await Promise.all(valid.map((f) => uploadSupportingFile(f)));
            // Refetch to get fresh signed URLs from BE rather than
            // patching state with what we already have — the BE may
            // have enriched filename / size / signed url.
            const failed = results.filter((r) => !r.success);
            if (failed.length > 0) {
                setUploadError(failed.map((r) => r.message).filter(Boolean).join(" ") || "Some uploads failed.");
            }
            await fetchFiles();
            setIsUploading(false);
        },
        [files.length, fetchFiles]
    );

    const confirmDelete = async () => {
        const file = pendingDelete;
        if (!file || !file.id) {
            setPendingDelete(null);
            return;
        }
        setDeletingIds((prev) => new Set(prev).add(file.id as string));
        const resp = await deleteSupportingFile(file.id);
        if (resp.success) {
            setFiles((prev) => prev.filter((f) => f.id !== file.id));
        } else {
            setUploadError(resp.message || "Failed to delete file.");
        }
        setDeletingIds((prev) => {
            const next = new Set(prev);
            next.delete(file.id as string);
            return next;
        });
        setPendingDelete(null);
    };

    const remainingSlots = Math.max(0, MAX_FILES - files.length);
    const canUpload = remainingSlots > 0 && !isUploading;

    return (
        <div className="bg-white dark:bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div className="flex-1">
                    <h2 className="font-medium text-gray-900">
                        Supporting Documents
                    </h2>
                    <p className="text-sm text-gray-600">
                        Transcripts, portfolios, certificates and other PDFs you've shared with employers.
                    </p>
                </div>
                {!isEditing && (
                    <div className="flex-shrink-0">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf"
                            multiple
                            className="hidden"
                            onChange={(e) => {
                                if (e.target.files && e.target.files.length > 0) {
                                    handleFiles(e.target.files);
                                }
                                if (fileInputRef.current) fileInputRef.current.value = "";
                            }}
                        />
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={!canUpload}
                            onClick={() => fileInputRef.current?.click()}
                            className="flex items-center gap-1 bg-white border-gray-300 text-gray-700 hover:bg-gray-100"
                        >
                            {isUploading ? (
                                <>
                                    <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                    Uploading…
                                </>
                            ) : (
                                <>
                                    <UploadIcon className="h-4 w-4" />
                                    Add document
                                </>
                            )}
                        </Button>
                    </div>
                )}
            </div>

            {/* Inline status messages */}
            {loadError && (
                <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
                    {loadError}
                </div>
            )}
            {uploadError && (
                <div className="mb-4 text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
                    {uploadError}
                </div>
            )}

            {/* Body */}
            {isLoading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[0, 1].map((i) => (
                        <div
                            key={i}
                            className="h-20 rounded-lg border border-gray-100 bg-gray-50 animate-pulse"
                        />
                    ))}
                </div>
            ) : files.length === 0 ? (
                <p className="text-sm text-gray-500">
                    No supporting documents yet. Add PDFs such as transcripts, portfolios, or certifications to give employers more context.
                </p>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {files.map((file) => {
                        const id = file.id || file.filename;
                        const isDeleting = file.id ? deletingIds.has(file.id) : false;
                        return (
                            <div
                                key={id}
                                className="group flex items-center gap-3 rounded-lg border border-gray-200 px-4 py-3 hover:border-blue-300 hover:shadow-sm transition-all"
                            >
                                <div className="h-10 w-10 rounded-md bg-blue-50 flex items-center justify-center flex-shrink-0">
                                    <FileTextIcon className="h-5 w-5 text-blue-600" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                        {file.original_name || file.filename}
                                    </p>
                                    <p className="text-xs text-gray-500 truncate">
                                        {formatBytes(file.size_bytes)}
                                        {file.uploaded_at && ` · Uploaded ${parseFormat(file.uploaded_at, "D MMM YYYY")}`}
                                    </p>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                    {file.url && (
                                        <button
                                            type="button"
                                            onClick={() => setPreviewFile(file)}
                                            className="p-2 rounded-md text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                                            aria-label={`Preview ${file.original_name || file.filename}`}
                                            title="Preview PDF"
                                        >
                                            <EyeIcon className="h-4 w-4" />
                                        </button>
                                    )}
                                    {!isEditing && (
                                        <button
                                            type="button"
                                            disabled={isDeleting}
                                            onClick={() => setPendingDelete(file)}
                                            className="p-2 rounded-md text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            aria-label={`Delete ${file.original_name || file.filename}`}
                                            title="Delete"
                                        >
                                            {isDeleting ? (
                                                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                            ) : (
                                                <Trash2Icon className="h-4 w-4" />
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Footer summary */}
            {!isLoading && files.length > 0 && (
                <p className="mt-3 text-xs text-gray-500">
                    {files.length} of {MAX_FILES} document{files.length === 1 ? "" : "s"} · PDF only, max 5 MB each
                </p>
            )}

            {/* Confirm-delete dialog. Stays mounted regardless of
                pendingDelete so the close animation runs cleanly. */}
            <AlertDialog
                open={!!pendingDelete}
                onOpenChange={(open) => {
                    if (!open) setPendingDelete(null);
                }}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this document?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {pendingDelete && (
                                <>
                                    <span className="font-medium text-gray-900">
                                        {pendingDelete.original_name || pendingDelete.filename}
                                    </span>{" "}
                                    will be permanently removed from your profile. This action cannot be undone.
                                </>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            disabled={
                                pendingDelete?.id ? deletingIds.has(pendingDelete.id) : false
                            }
                        >
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                // Keep the dialog open while the request
                                // is in flight — confirmDelete closes it
                                // itself when the API resolves.
                                e.preventDefault();
                                confirmDelete();
                            }}
                            disabled={
                                pendingDelete?.id ? deletingIds.has(pendingDelete.id) : false
                            }
                            className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
                        >
                            {pendingDelete?.id && deletingIds.has(pendingDelete.id) ? (
                                <>
                                    <span className="inline-block h-4 w-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                    Deleting…
                                </>
                            ) : (
                                "Delete"
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* PDF preview drawer */}
            <Sheet
                open={!!previewFile}
                onOpenChange={(open) => {
                    if (!open) setPreviewFile(null);
                }}
            >
                <SheetContent
                    side="right"
                    className="w-full sm:max-w-[600px] md:max-w-[700px] lg:max-w-[50vw] flex flex-col p-0"
                >
                    <SheetHeader className="px-5 py-4 border-b border-gray-100 flex-shrink-0">
                        <SheetTitle className="text-sm font-semibold text-gray-900 flex items-center gap-2 truncate">
                            <FileTextIcon className="w-4 h-4 text-blue-600" />
                            <span className="truncate">
                                {previewFile?.original_name || previewFile?.filename}
                            </span>
                        </SheetTitle>
                    </SheetHeader>
                    <div className="flex-1 min-h-0 overflow-hidden">
                        {previewFile?.url && (
                            <PDFViewer url={previewFile.url} className="h-full rounded-none" />
                        )}
                    </div>
                </SheetContent>
            </Sheet>
        </div>
    );
}
